// eslint-disable-next-line no-restricted-imports
import {
  BLOCK_STATUS_ACTIVE,
  COMMENT_STATUS_ACTIVE,
  EVENT_DOWNVOTE_STORY,
  EVENT_LIKE_STORY,
  EVENT_REMOVE_VOTE_FROM_STORY,
  EVENT_UNLIKE_STORY,
  EVENT_UPVOTE_STORY,
  KIND_LIKE,
  KIND_VOTE,
  STORY_COMMENT_ENTITY_TYPE,
  STORY_ENGAGEMENT_APP,
  STORY_THREAD_ENTITY_TYPE,
  USER_BLOCKING_APP,
  USER_BLOCK_ENTITY_TYPE,
} from "../constants/story-engagement-constants.mjs";

export const VOTE_UP = 1;
export const VOTE_NONE = 0;
export const VOTE_DOWN = -1;

const STORY_VOTE_EVENTS = { [VOTE_UP]: EVENT_UPVOTE_STORY, [VOTE_DOWN]: EVENT_DOWNVOTE_STORY, [VOTE_NONE]: EVENT_REMOVE_VOTE_FROM_STORY };
const MSG_NO_STORY_EVENT = "A story tap has no StoryEngagement event; expected a like (true/false) or a vote (1, 0, -1)";
const REQUIRED_COMMUNITY_POST_FIELDS = ["likeStatus", "numLikes", "voteStatus", "voteScore", "upvoteCount", "downvoteCount"];

export class StoryEngagementError extends Error {
  constructor(message, data = {}) {
    super(message);
    this.name = "StoryEngagementError";
    this.data = data;
  }
}

export function engagementButtonLabel(label, shownCount) {
  if (!label) {
    return null;
  }
  return shownCount === "" || shownCount === null || shownCount === undefined ? label : `${label}, ${shownCount}`;
}

export const SHARE_BY_SHEET = "sheet";
export const SHARE_BY_COPYING = "copy";

export function shareRouteFor({ hasShareSheet, touchFirst }) {
  return hasShareSheet && touchFirst ? SHARE_BY_SHEET : SHARE_BY_COPYING;
}

export function isLiked(likeStatus) {
  return likeStatus === true || Number(likeStatus) === 1;
}

export function voteAfterTap(currentVote, tappedVote) {
  return Number(currentVote) === tappedVote ? VOTE_NONE : tappedVote;
}

export function isCountShown({ editMode, featureOn, count, hideAtZero }) {
  if (editMode) {
    return true;
  }
  if (!featureOn) {
    return false;
  }
  return !(hideAtZero && Number(count ?? 0) === 0);
}

export const DISPLAY_VALUE_HIDDEN = "hide";

export function countToShow(count, displayValue) {
  if (displayValue === DISPLAY_VALUE_HIDDEN) {
    return "";
  }
  const shown = Number(count ?? 0);
  if (Number.isNaN(shown)) {
    throw new Error(`An engagement count is not a number: ${JSON.stringify(count)} — fix the story record that sent it`);
  }
  return shown;
}

export function predictLikeFields(fields, liked) {
  const wasLiked = isLiked(fields.likeStatus);
  if (wasLiked === liked) {
    return { ...fields, likeStatus: liked };
  }
  const likeCount = Math.max(0, Number(fields.likeCount ?? 0) + (liked ? 1 : -1));
  return { ...fields, likeStatus: liked, likeCount };
}

export function predictVoteFields(fields, vote) {
  const previous = Number(fields.voteStatus ?? VOTE_NONE);
  let upvoteCount = Number(fields.upvoteCount ?? 0);
  let downvoteCount = Number(fields.downvoteCount ?? 0);
  if (previous === VOTE_UP) {
    upvoteCount -= 1;
  }
  if (previous === VOTE_DOWN) {
    downvoteCount -= 1;
  }
  if (vote === VOTE_UP) {
    upvoteCount += 1;
  }
  if (vote === VOTE_DOWN) {
    downvoteCount += 1;
  }
  return {
    ...fields,
    voteStatus: vote,
    voteScore: Number(fields.voteScore ?? 0) - previous + vote,
    upvoteCount: Math.max(0, upvoteCount),
    downvoteCount: Math.max(0, downvoteCount),
  };
}

export function engagementFieldsFromCommunityPost(post) {
  if (!post || typeof post !== "object") {
    throw new Error("The like/vote answer carried no community post for the story");
  }
  const missing = REQUIRED_COMMUNITY_POST_FIELDS.filter(field => post[field] === undefined);
  if (missing.length > 0) {
    throw new Error(`The like/vote answer's community post is missing ${missing.join(", ")}`);
  }
  return {
    likeStatus: isLiked(post.likeStatus),
    likeCount: Number(post.numLikes),
    voteStatus: Number(post.voteStatus),
    voteScore: Number(post.voteScore),
    upvoteCount: Number(post.upvoteCount),
    downvoteCount: Number(post.downvoteCount),
  };
}

function targetReached(kind, fields, target) {
  return kind === KIND_LIKE ? isLiked(fields.likeStatus) === target : Number(fields.voteStatus) === target;
}

function predictFields(kind, fields, target) {
  return kind === KIND_LIKE ? predictLikeFields(fields, target) : predictVoteFields(fields, target);
}

export function createEngagementQueue({ send, apply, report }) {
  const entries = new Map();

  function entryFor(kind, storyId, fields) {
    const key = `${kind}:${storyId}`;
    const existing = entries.get(key);
    if (existing?.inFlight) {
      return existing;
    }
    const entry = { key, kind, storyId, confirmed: { ...fields }, shown: { ...fields }, target: null, inFlight: false };
    entries.set(key, entry);
    return entry;
  }

  function show(entry, fields) {
    entry.shown = fields;
    apply(entry.storyId, fields);
  }

  function flush(entry) {
    const target = entry.target;
    entry.inFlight = true;
    send(
      entry.kind,
      entry.storyId,
      target,
      answer => {
        entry.inFlight = false;
        entry.confirmed = { ...answer };
        if (targetReached(entry.kind, answer, entry.target)) {
          entries.delete(entry.key);
          show(entry, { ...answer });
          return;
        }
        show(entry, predictFields(entry.kind, answer, entry.target));
        flush(entry);
      },
      error => {
        entry.inFlight = false;
        entries.delete(entry.key);
        show(entry, { ...entry.confirmed });
        report(error, { storyId: entry.storyId, kind: entry.kind, target });
      },
    );
  }

  function tap(kind, storyId, fields, target) {
    const entry = entryFor(kind, storyId, fields);
    entry.target = target;
    show(entry, predictFields(kind, entry.shown, target));
    if (!entry.inFlight) {
      flush(entry);
    }
  }

  return {
    tapLike(storyId, fields) {
      const entry = entryFor(KIND_LIKE, storyId, fields);
      tap(KIND_LIKE, storyId, fields, !isLiked(entry.shown.likeStatus));
    },
    tapVote(storyId, fields, direction) {
      const entry = entryFor(KIND_VOTE, storyId, fields);
      tap(KIND_VOTE, storyId, fields, voteAfterTap(entry.shown.voteStatus, direction));
    },
  };
}

export function engagesThroughStoryEngagement({ renderSurface, nativeOn, appInScope, destinationId, storyPublicationId }) {
  return renderSurface && nativeOn && appInScope && destinationId > 0 && storyPublicationId === destinationId;
}

export function storyEngagementEventFor(kind, target) {
  if (kind === KIND_LIKE) {
    return target ? EVENT_LIKE_STORY : EVENT_UNLIKE_STORY;
  }
  if (kind === KIND_VOTE && Object.hasOwn(STORY_VOTE_EVENTS, target)) {
    return STORY_VOTE_EVENTS[target];
  }
  throw new StoryEngagementError(MSG_NO_STORY_EVENT, { kind, target });
}

export function buildStoryEngagementEvent(kind, storyId, target) {
  return { eventType: storyEngagementEventFor(kind, target), params: { story: Number(storyId) } };
}

export function buildConfirmedEngagementFields(kind, target, shown, counts) {
  if (kind === KIND_LIKE) {
    return counts ? { ...shown, likeStatus: target, likeCount: counts.likeCount } : { ...shown, likeStatus: target };
  }
  if (!counts) {
    return { ...shown, voteStatus: target };
  }
  const { upvoteCount, downvoteCount } = counts;
  return { ...shown, voteStatus: target, upvoteCount, downvoteCount, voteScore: upvoteCount - downvoteCount };
}

function findHiddenCommentIds(transaction, comments) {
  const visible = new Set(comments.filter(comment => comment.active).map(comment => comment.id));
  return Object.values(transaction.subjects)
      .filter(subject => subject.entityType === STORY_COMMENT_ENTITY_TYPE)
      .flatMap(subject => subject.pks)
      .map(pk => Number(pk.id))
      .filter(commentId => !visible.has(commentId));
}

export function collectOtherReadersEngagement(transaction, readerId) {
  const rows = transaction.output[STORY_ENGAGEMENT_APP];
  const isOwnTransaction = readerId > 0 && transaction.userId === readerId;
  if (!rows || isOwnTransaction) {
    return { totals: [], commentRefreshes: [] };
  }
  const threads = Object.values(rows[STORY_THREAD_ENTITY_TYPE] ?? {});
  const totals = threads.map(thread => {
    const upvoteCount = Number(thread.upvoteCount);
    const downvoteCount = Number(thread.downvoteCount);
    return {
      storyId: Number(thread.storyId),
      likeCount: Number(thread.likeCount),
      upvoteCount,
      downvoteCount,
      voteScore: upvoteCount - downvoteCount,
      commentCount: Number(thread.commentCount),
    };
  });
  const comments = Object.values(rows[STORY_COMMENT_ENTITY_TYPE] ?? {}).map(comment => ({
    id: Number(comment.id),
    storyId: Number(comment.thread.storyId),
    active: comment.status === COMMENT_STATUS_ACTIVE,
  }));
  const removedByStory = new Map();
  const removedFrom = storyId => {
    if (!removedByStory.has(storyId)) {
      removedByStory.set(storyId, []);
    }
    return removedByStory.get(storyId);
  };
  for (const comment of comments) {
    const removed = removedFrom(comment.storyId);
    if (!comment.active) {
      removed.push(comment.id);
    }
  }
  const hiddenCommentIds = findHiddenCommentIds(transaction, comments);
  if (hiddenCommentIds.length > 0) {
    totals.forEach(total => removedFrom(total.storyId).push(...hiddenCommentIds));
  }
  const commentRefreshes = [...removedByStory].map(([storyId, removedCommentIds]) => ({
    storyId,
    removedCommentIds: [...new Set(removedCommentIds)],
  }));
  return { totals, commentRefreshes };
}

export function collectReadersBlockChanges(transaction, readerId) {
  const changes = { blocked: [], unblocked: [] };
  const blocks = transaction.output[USER_BLOCKING_APP]?.[USER_BLOCK_ENTITY_TYPE] ?? {};
  for (const block of Object.values(blocks)) {
    if (block.blocker.id === readerId) {
      (block.status === BLOCK_STATUS_ACTIVE ? changes.blocked : changes.unblocked).push(block.blocked.id);
    }
  }
  return changes;
}
